import { Component, ElementRef, HostListener, NgZone, OnDestroy, effect, inject, signal } from '@angular/core';
import { AgentSpotlightService, SpotlightStep, onPage } from '../../agent/agent-spotlight.service';
import { resolveAgentElement } from '../../agent/agent-ui.service';

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

const FIND_TIMEOUT_MS = 3000;
const ALT_AFTER_MS = 2500;
// long enough to read the note (6 s were too short in the first live test, 17.10.2026)
const HIGHLIGHT_MS = 10000;
const PADDING = 6;
const BUBBLE_WIDTH = 300;
/** Distance of the note from the element and from the window edge. */
const GAP = 12;
/** Until the note has rendered once and can be measured. */
const BUBBLE_HEIGHT_GUESS = 200;

/**
 * Draws what the in-house agent points at (AgentSpotlightService): a ring
 * around the element and a bubble with its note. A tour dims the rest of the
 * page and steps through; a highlight does not dim and goes away by itself.
 *
 * Mounted once in the dashboard layout, inside `nb-layout-column` like
 * `app-portal-hints` (fixed position, inside the `.al-portal` token scope).
 * Its z-index sits below the CDK overlay group (12000) so dialogs and menus
 * stay on top; it never sets transform/filter on an ancestor (CLAUDE.md
 * rule 12). The box follows the element while it moves (scroll, resize,
 * layout) via one animation-frame loop outside Angular's zone.
 */
@Component({
  selector: 'app-agent-spotlight',
  templateUrl: './agent-spotlight.component.html',
  styleUrls: ['./agent-spotlight.component.scss'],
})
export class AgentSpotlightComponent implements OnDestroy {
  readonly spotlight = inject(AgentSpotlightService);
  private readonly zone = inject(NgZone);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly box = signal<Box | null>(null);
  readonly bubble = signal<{ top: number; left: number } | null>(null);
  /** Size of the note as last placed — a change (text, tip, window) places it again. */
  private bubbleSize = '';

  private frame = 0;
  private stepKey = '';
  private searchingSince = 0;
  private element: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private clickTarget: HTMLElement | null = null;
  private marked: HTMLElement | null = null;
  private wasElsewhere = false;
  private readonly onTargetClick = () => this.zone.run(() => this.spotlight.next());
  /** A waiting step whose element is not there yet: the note stands on its own at the bottom. */
  readonly waiting = signal(false);
  /** The element sits in a dialog/dropdown (CDK overlay) — ring and note go above it. */
  readonly overlay = signal(false);

  constructor() {
    effect(() => {
      const state = this.spotlight.state();
      this.stop();
      if (!state) return;
      const step = state.steps[state.index];
      this.stepKey = `${state.kind}:${state.index}:${step.anchor ?? step.target}`;
      this.element = null;
      this.wasElsewhere = false;
      this.searchingSince = performance.now();
      if (state.kind === 'highlight') {
        this.hideTimer = setTimeout(() => this.spotlight.close(), HIGHLIGHT_MS);
      }
      // Already done (the group is open): move on without asking.
      if (step.skipIfVisible && resolveAgentElement({ anchor: step.skipIfVisible })) {
        queueMicrotask(() => this.spotlight.next());
        return;
      }
      this.zone.runOutsideAngular(() => this.track(step, this.stepKey));
    }, { allowSignalWrites: true });
  }

  get step(): SpotlightStep | null {
    const s = this.spotlight.state();
    return s ? s.steps[s.index] : null;
  }

  private track(step: SpotlightStep, key: string): void {
    const tick = () => {
      if (key !== this.stepKey) return;
      if (!this.element || !this.element.isConnected) {
        // A step that belongs to another page waits there instead of being
        // dropped — the person may still be on the way (or went back).
        const elsewhere = !!step.page && !onPage(step.page, location.pathname);
        // Just arrived on the step's page: its lists start loading now.
        if (this.wasElsewhere && !elsewhere) this.searchingSince = performance.now();
        this.wasElsewhere = elsewhere;
        // A fallback only once the page had time to render the real target —
        // lists load after the page, and the first tick must not settle for
        // the fallback (Twin tile still loading → „Hinzufügen“ ringed).
        const altDue = performance.now() - this.searchingSince > ALT_AFTER_MS;
        this.element = elsewhere ? null : resolveAgentElement(step) ?? (altDue ? this.firstAlt(step) : null);
        if (this.element) {
          this.mark(this.element);
          const inOverlay = !!this.element.closest('.cdk-overlay-container');
          if (inOverlay !== this.overlay()) this.zone.run(() => this.overlay.set(inOverlay));
          this.element.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
          if (step.advance === 'click') this.listen(this.element);
          if (this.waiting()) this.zone.run(() => this.waiting.set(false));
        } else if (step.await || elsewhere) {
          // Academy click paths: the person opens it themselves — keep looking,
          // show the note without a ring meanwhile.
          if (!this.waiting() && performance.now() - this.searchingSince > 500) {
            this.zone.run(() => {
              this.waiting.set(true);
              this.box.set(null);
            });
          }
        } else if (performance.now() - this.searchingSince > FIND_TIMEOUT_MS) {
          // The element never appeared (other page, hidden by role): drop the step.
          this.zone.run(() => this.spotlight.skip());
          return;
        }
      }
      if (this.element) this.place(this.element.getBoundingClientRect());
      else if (this.waiting()) this.placeAlone();
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  private place(rect: DOMRect): void {
    const box = {
      top: rect.top - PADDING,
      left: rect.left - PADDING,
      width: rect.width + PADDING * 2,
      height: rect.height + PADDING * 2,
    };
    const { width: w, height: h } = this.measureBubble();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const size = `${w}x${h}@${vw}x${vh}`;
    const current = this.box();
    if (
      current && size === this.bubbleSize &&
      current.top === box.top && current.left === box.left && current.width === box.width && current.height === box.height
    ) {
      return;
    }
    this.bubbleSize = size;
    // Placed with the note's measured size, never a guessed height: with a tip
    // and the buttons it is taller than the old 160–220px assumptions and stuck
    // out above or below the window (Marcel, 31.10.2026). Order: below, above,
    // right, left of the element — the first place where it fits whole.
    const below = box.top + box.height + GAP;
    const aboveTop = box.top - GAP - h;
    const clampLeft = (x: number) => Math.max(GAP, Math.min(x, vw - w - GAP));
    const clampTop = (y: number) => Math.max(GAP, Math.min(y, vh - h - GAP));
    let top: number;
    let left: number;
    if (below + h <= vh - GAP) {
      top = below;
      left = clampLeft(box.left);
    } else if (aboveTop >= GAP) {
      top = aboveTop;
      left = clampLeft(box.left);
    } else if (box.left + box.width + GAP + w <= vw - GAP) {
      top = clampTop(box.top);
      left = box.left + box.width + GAP;
    } else if (box.left - GAP - w >= GAP) {
      top = clampTop(box.top);
      left = box.left - GAP - w;
    } else {
      // Element almost as big as the window (a whole card): nothing fits
      // beside it — keep the note whole on screen, over its lower part.
      top = clampTop(below);
      left = clampLeft(box.left);
    }
    // An element still partly outside the window (scrolling towards it, a
    // sticky header) must not drag the note along — it stays inside.
    top = clampTop(top);
    this.zone.run(() => {
      this.box.set(box);
      this.bubble.set({ top, left });
    });
  }

  /** Waiting for the element: the note stands on its own, centred at the bottom. */
  private placeAlone(): void {
    const { width: w, height: h } = this.measureBubble();
    const top = Math.max(GAP, window.innerHeight - h - 2 * GAP);
    const left = Math.max(GAP, (window.innerWidth - w) / 2);
    const current = this.bubble();
    if (current && current.top === top && current.left === left) return;
    this.zone.run(() => this.bubble.set({ top, left }));
  }

  private measureBubble(): { width: number; height: number } {
    const el = this.host.nativeElement.querySelector<HTMLElement>('.agent-spotlight__bubble');
    return {
      width: el?.offsetWidth || Math.min(BUBBLE_WIDTH, window.innerWidth - 2 * GAP),
      height: el?.offsetHeight || BUBBLE_HEIGHT_GUESS,
    };
  }

  private firstAlt(step: SpotlightStep): HTMLElement | null {
    for (const anchor of step.alt ?? []) {
      const el = resolveAgentElement({ anchor });
      if (el) return el;
    }
    return null;
  }

  /** Tells the page which element is pointed at (`data-al-spotlit`), so a
   *  control that only shows on hover can show itself (agent tile actions). */
  private mark(el: HTMLElement | null): void {
    this.marked?.removeAttribute('data-al-spotlit');
    this.marked = el;
    el?.setAttribute('data-al-spotlit', '');
  }

  /** `advance: 'click'`: the person's own click on the element is the step. */
  private listen(el: HTMLElement): void {
    this.clickTarget?.removeEventListener('click', this.onTargetClick, true);
    this.clickTarget = el;
    el.addEventListener('click', this.onTargetClick, true);
  }

  private stop(): void {
    this.mark(null);
    this.clickTarget?.removeEventListener('click', this.onTargetClick, true);
    this.clickTarget = null;
    this.waiting.set(false);
    this.overlay.set(false);
    cancelAnimationFrame(this.frame);
    if (this.hideTimer) clearTimeout(this.hideTimer);
    this.hideTimer = null;
    this.box.set(null);
    this.bubble.set(null);
    this.bubbleSize = '';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.spotlight.state()) this.spotlight.close();
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
